import React, { useState } from 'react';
import { Drawer as VaulDrawer } from 'vaul';
import { DrawerProps } from './Drawer.types';
import { classes, st } from './Drawer.st.css';
import PropTypes from 'prop-types';
import { DATA_HOOKS } from './Drawer.constants';
import { ZIndex } from '../common/ZIndex';

const Drawer = ({
  open,
  backdrop = true,
  renderBackdrop = true,
  resizable = true,
  margin = true,
  onClose,
  role = 'dialog',
  ariaLabelledBy,
  children,
  snapPoints,
  dismissible = true,
  dataHook,
  zIndex = ZIndex.popover,
  scrollable = true,
  repositionInputs = true,
}: DrawerProps) => {
  const [snap, setSnap] = useState<number | string | null>(
    snapPoints?.[0] ?? null,
  );

  const snapProps = snapPoints?.length
    ? {
        snapPoints,
        activeSnapPoint: snap,
        setActiveSnapPoint: setSnap,
        fadeFromIndex: 0,
      }
    : {};

  return (
    <div data-hook={dataHook}>
      <VaulDrawer.Root
        open={open}
        onOpenChange={onClose}
        container={typeof window !== 'undefined' ? window.document.body : null}
        handleOnly={true}
        repositionInputs={repositionInputs}
        {...snapProps}
      >
        <VaulDrawer.Portal>
          {renderBackdrop && (
            <VaulDrawer.Overlay
              data-hook={DATA_HOOKS.OVERLAY}
              className={st(classes.overlay, {
                backdrop,
              })}
              style={{ zIndex }}
            />
          )}
          <VaulDrawer.Content
            data-hook={DATA_HOOKS.CONTENT}
            aria-modal="true"
            aria-labelledby={ariaLabelledBy}
            role={role}
            className={st(classes.content, { margin })}
            onInteractOutside={
              dismissible ? undefined : e => e.preventDefault()
            }
            style={{ zIndex }}
          >
            {/* Vaul (Radix Dialog) requires a title. Otherwise, it will throw an error. It's visually hidden. */}
            <VaulDrawer.Title className={classes.title} />
            {resizable && (
              <VaulDrawer.Handle
                className={classes.handle}
                data-hook={DATA_HOOKS.HANDLE}
              />
            )}
            {scrollable ? (
              <div className={st(classes.contentWrapper, { margin })}>
                {children}
              </div>
            ) : (
              children
            )}
          </VaulDrawer.Content>
        </VaulDrawer.Portal>
      </VaulDrawer.Root>
    </div>
  );
};

Drawer.displayName = 'Drawer';

Drawer.propTypes = {
  dataHook: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  backdrop: PropTypes.bool,
  renderBackdrop: PropTypes.bool,
  resizable: PropTypes.bool,
  margin: PropTypes.bool,
  role: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  children: PropTypes.node.isRequired,
  snapPoints: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ),
  dismissible: PropTypes.bool,
  scrollable: PropTypes.bool,
  repositionInputs: PropTypes.bool,
};

export default Drawer;
